// URL state for the /versions route — project + namespace multi-select
// as comma-separated lists, shareable independently of /compare.

export const CHART_VERSION_TABLE = "chart-version-table" as const;
export const CHART_UPDATES_AVAILABLE = "chart-updates-available" as const;

export type VersionView =
  | typeof CHART_VERSION_TABLE
  | typeof CHART_UPDATES_AVAILABLE;

export interface VersionSearch {
  projects: string[];
  namespaces: string[];
  view: VersionView;
  project: string;
}

interface RawSearch {
  projects?: string | null;
  namespaces?: string | null;
  view?: string | null;
  project?: string | null;
}

export function parseVersionSearch(
  input: RawSearch | URLSearchParams,
): VersionSearch {
  const raw = isUrlSearchParams(input) ? readFromUrlSearchParams(input) : input;
  return {
    projects: parseList(raw.projects),
    namespaces: parseList(raw.namespaces),
    view: parseView(raw.view),
    project: parseProject(raw.project),
  };
}

export function formatVersionSearch(state: VersionSearch): URLSearchParams {
  const out = new URLSearchParams();
  if (state.projects.length > 0) {
    out.set("projects", state.projects.join(","));
  }
  if (state.namespaces.length > 0) {
    out.set("namespaces", state.namespaces.join(","));
  }
  if (state.view === CHART_UPDATES_AVAILABLE) {
    out.set("view", state.view);
  }
  if (state.project) {
    out.set("project", state.project);
  }
  return out;
}

function parseView(raw: string | null | undefined): VersionView {
  if (raw === CHART_UPDATES_AVAILABLE) return CHART_UPDATES_AVAILABLE;
  return CHART_VERSION_TABLE;
}

function parseProject(raw: string | null | undefined): string {
  return typeof raw === "string" ? raw.trim() : "";
}

export function versionSearchToString(state: VersionSearch): string {
  const qs = formatVersionSearch(state).toString();
  return qs ? `?${qs}` : "";
}

function parseList(raw: string | null | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const source = typeof raw === "string" ? raw : "";
  for (const part of source.split(",")) {
    const value = part.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

function isUrlSearchParams(value: unknown): value is URLSearchParams {
  return (
    typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams
  );
}

function readFromUrlSearchParams(params: URLSearchParams): RawSearch {
  return {
    projects: params.get("projects"),
    namespaces: params.get("namespaces"),
    view: params.get("view"),
    project: params.get("project"),
  };
}
